import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import EditNoteIcon from '@mui/icons-material/EditNote';
import XIcon from '@mui/icons-material/X';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { useState } from 'react';
import { db } from '../../../core/services/firebase';

const FONT_TIME = "'Barlow Condensed', sans-serif";
const FONT_BODY = "'Zen Kaku Gothic New', sans-serif";
const GRADIENT = 'linear-gradient(135deg, #8a2be2, #FFD700)';

const timeValueStyle = {
  fontFamily: FONT_TIME,
  fontSize: '1.75rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  lineHeight: 1.1,
  background: 'linear-gradient(135deg, #d4a0ff 0%, #FFD700 50%, #ffeaa0 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  filter: 'drop-shadow(0 0 12px rgba(255,215,0,0.1))',
};

const ghostButtonStyle = {
  flex: 1,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  color: 'rgba(255,255,255,0.8)',
  bgcolor: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px',
  textTransform: 'none',
  '&:hover': {
    bgcolor: 'rgba(138,43,226,0.1)',
    borderColor: 'rgba(138,43,226,0.2)',
    color: '#fff',
  },
};

const downloadButtonStyle = {
  flex: 1,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  color: '#fff',
  background: GRADIENT,
  borderRadius: '10px',
  textTransform: 'none',
  boxShadow: '0 2px 12px rgba(138,43,226,0.2)',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(138,43,226,0.35)',
    transform: 'translateY(-1px)',
  },
};

export function CertificateDetailDialog({ certificate, open, onClose, onDeleted, onTweet, onMemo }) {
  const [deleting, setDeleting] = useState(false);

  if (!certificate) return null;

  const { thumbnailURL, mediaURL, mediaType, eventDate, eventName, time, netTime } = certificate;
  const dateStr = eventDate?.toDate?.()
    ? eventDate.toDate().toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric' })
    : '';

  const handleDownload = async (source, ext) => {
    const response = await fetch(source);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `certificate.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDelete = async () => {
    if (!window.confirm('本当に削除しますか？')) return;
    setDeleting(true);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'certificates', certificate.id), { deletedAt: serverTimestamp() });
      batch.set(doc(collection(db, 'certificateTasks')), {
        docId: certificate.id,
        certificateId: certificate.certificateId,
        taskType: 'deleteCertificate',
        startedAt: serverTimestamp(),
        completedAt: null,
        failedAt: null,
        reason: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        deletedAt: null,
      });
      await batch.commit();
      onDeleted(certificate.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete certificate:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {/* Hero image area */}
      <Box
        sx={{
          position: 'relative',
          background: 'radial-gradient(ellipse at 50% 50%, rgba(138,43,226,0.04) 0%, transparent 70%), #0c0c14',
          p: 3,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {/* Floating close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 32,
            height: 32,
            bgcolor: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.7)',
            zIndex: 3,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)', color: '#fff' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>

        {mediaType === 'video' ? (
          <Box
            component="video"
            src={mediaURL}
            controls
            autoPlay
            sx={{ width: '80%', maxWidth: 320, borderRadius: 2 }}
          />
        ) : (
          <Box
            component="img"
            src={thumbnailURL}
            sx={{
              width: '70%',
              maxWidth: 260,
              borderRadius: '4px',
              filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.5))',
            }}
          />
        )}

        {/* Bottom vignette */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 40,
            background: 'linear-gradient(to top, #12121A, transparent)',
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {/* Event info */}
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontSize: '0.7rem',
            color: 'text.secondary',
            letterSpacing: '0.12em',
          }}
        >
          {dateStr}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontSize: '1.2rem',
            fontWeight: 700,
            color: 'text.primary',
            lineHeight: 1.25,
            mt: 0.5,
          }}
        >
          {eventName}
        </Typography>

        {/* Times — side by side */}
        <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
          {[
            { label: 'Gross', value: time },
            { label: 'Net', value: netTime },
          ].map(({ label, value }) => (
            <Box key={label}>
              <Typography
                sx={{
                  fontFamily: FONT_BODY,
                  fontSize: '0.55rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                }}
              >
                {label}
              </Typography>
              <Typography sx={timeValueStyle}>{value}</Typography>
            </Box>
          ))}
        </Box>

        {/* X share + Memo */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
          <Button size="small" startIcon={<XIcon />} onClick={() => onTweet(certificate)} sx={ghostButtonStyle}>
            Xに投稿
          </Button>
          <Button size="small" startIcon={<EditNoteIcon />} onClick={() => onMemo(certificate)} sx={ghostButtonStyle}>
            メモ
          </Button>
        </Box>

        {/* Download buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
          {mediaType === 'pdf' && mediaURL && (
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(mediaURL, 'pdf')}
              sx={downloadButtonStyle}
            >
              PDF
            </Button>
          )}
          {mediaType === 'video' && mediaURL && (
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(mediaURL, 'mp4')}
              sx={downloadButtonStyle}
            >
              動画
            </Button>
          )}
          {thumbnailURL && (
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(thumbnailURL, 'png')}
              sx={downloadButtonStyle}
            >
              画像
            </Button>
          )}
        </Box>

        {/* Delete */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            size="small"
            onClick={handleDelete}
            disabled={deleting}
            sx={{
              fontFamily: FONT_BODY,
              fontSize: '0.7rem',
              fontWeight: 500,
              color: 'rgba(255,68,68,0.5)',
              textTransform: 'none',
              '&:hover': { color: 'rgba(255,68,68,0.85)', bgcolor: 'transparent' },
            }}
          >
            {deleting ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null}
            記録を削除
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
